'use client'

import { useCallback, useEffect, useRef } from 'react'

import { chartBarStartMs, nextChartBarStartMs } from '@/lib/chart-timeframe'
import { qualifyChartSymbol } from '@/lib/tradingview/chart-symbol-identity'
import { extractBarAnalytics } from '@/lib/tradingview/datafeeds/candle-analytics'
import { AnalyticsBar, ChartDatafeed } from '@/lib/tradingview/datafeeds/chart-datafeed'
import { useChartStream } from '@/service/ws/use-chart-stream'
import { Exchange } from '@/types/generated/common/types'
import { ChartData } from '@/types/generated/public_api'

const MAX_BACKFILL_BARS = 500
// Bounds how long a single backfill REST call can hold up the shared message
// queue (see `messageQueueRef`) before it's abandoned as failed.
const BACKFILL_FETCH_TIMEOUT_MS = 5_000

interface UseChartRealtimeBridgeOptions {
  datafeedRef: React.RefObject<ChartDatafeed | null>
  isChartReady: boolean
  enabled?: boolean
  symbol: string
  exchangeEnum: Exchange
  timeframe: number
  lastPushedBarTimeRef: React.RefObject<number>
  onPriceUpdate?: (price: number) => void
  /** Fired once per closed bar per symbol+timeframe: a pushed bar with
   *  bar.time > lastPushedBarTimeRef means the previous bucket just closed.
   *  Guarded > 0 — the ref resets on symbol/timeframe change. */
  onBarClose?: (closedBarTimeMs: number) => void
}

export function useChartRealtimeBridge({
  datafeedRef,
  isChartReady,
  enabled = true,
  symbol,
  exchangeEnum,
  timeframe,
  lastPushedBarTimeRef,
  onPriceUpdate,
  onBarClose,
}: UseChartRealtimeBridgeOptions): void {
  const messageQueueRef = useRef(Promise.resolve())
  const previousConnectionStatusRef = useRef<string | null>(null)
  const activeStreamKey = `${symbol}|${exchangeEnum}|${timeframe}`
  const activeStreamKeyRef = useRef(activeStreamKey)
  activeStreamKeyRef.current = activeStreamKey

  const pushBars = useCallback(
    (bars: AnalyticsBar[]) => {
      const datafeed = datafeedRef.current
      if (!datafeed) return

      for (const bar of bars) {
        if (bar.time < lastPushedBarTimeRef.current) continue

        if (bar.time > lastPushedBarTimeRef.current) {
          if (lastPushedBarTimeRef.current > 0) {
            onBarClose?.(lastPushedBarTimeRef.current)
          }
          lastPushedBarTimeRef.current = bar.time
        }

        datafeed.pushBar(bar, {
          symbol,
          routingSymbol: qualifyChartSymbol({ exchange: exchangeEnum, symbol }),
          timeframeSeconds: timeframe,
        })
      }
    },
    [datafeedRef, exchangeEnum, lastPushedBarTimeRef, onBarClose, symbol, timeframe]
  )

  const backfillMissingBars = useCallback(
    async (toTimeMs: number) => {
      const datafeed = datafeedRef.current
      const lastPushedTimeMs = lastPushedBarTimeRef.current
      const timeframeMs = Math.max(1, timeframe) * 1000

      if (!datafeed || lastPushedTimeMs <= 0) return

      const requestStreamKey = activeStreamKeyRef.current
      const fromTimeMs = nextChartBarStartMs(lastPushedTimeMs, timeframe)
      const alignedToTimeMs = chartBarStartMs(toTimeMs, timeframe)
      if (fromTimeMs > alignedToTimeMs) return

      const estimatedBars = Math.ceil((alignedToTimeMs - fromTimeMs) / timeframeMs) + 1
      const fromSeconds = Math.floor(fromTimeMs / 1000)
      const toSeconds = Math.floor(alignedToTimeMs / 1000)

      // `processChartMessage` chains every live tick through the same
      // `messageQueueRef`, and this backfill runs inside that chain — a
      // request that never settles (dropped response, stalled connection)
      // would otherwise wedge the queue forever, silently blocking every
      // subsequent live update. Abort it after a bounded wait so the queue
      // always frees up; the ordinary catch below turns that into just
      // another failed-backfill warning.
      const timeoutSignal = AbortSignal.timeout(BACKFILL_FETCH_TIMEOUT_MS)

      try {
        const bars = await datafeed.fetchBars(
          {
            identity: { exchange: exchangeEnum, symbol },
            timeframe,
            from: fromSeconds,
            to: toSeconds,
            limit: Math.min(Math.max(estimatedBars, 1), MAX_BACKFILL_BARS),
          },
          timeoutSignal
        )

        if (activeStreamKeyRef.current !== requestStreamKey) return

        const missingBars = bars
          .filter((bar) => bar.time > lastPushedBarTimeRef.current && bar.time <= alignedToTimeMs)
          .sort((a, b) => a.time - b.time)

        pushBars(missingBars)
      } catch (error) {
        console.warn('[useChartRealtimeBridge] Failed to backfill chart bars:', error)
      }
    },
    [datafeedRef, exchangeEnum, lastPushedBarTimeRef, pushBars, symbol, timeframe]
  )

  const processChartMessage = useCallback(
    async (chartData: ChartData | undefined) => {
      if (!chartData?.candles || !datafeedRef.current) return
      if (activeStreamKeyRef.current !== activeStreamKey) return

      const matchesActiveSymbol = chartData.symbol === symbol
      const matchesActiveExchange = chartData.exchange === exchangeEnum
      const matchesActiveTimeframe = chartData.timeframe === timeframe

      if (!matchesActiveSymbol || !matchesActiveExchange || !matchesActiveTimeframe) {
        return
      }

      const candleList = chartData.candles
      const { ticks, open, high, low, close, volumeBase } = candleList
      if (ticks.length === 0) return

      const barsByBucket = new Map<number, AnalyticsBar>()

      ticks.forEach((tick, i) => {
        const rawTime = Number(tick) * 1000
        if (!Number.isFinite(rawTime)) return

        const alignedTime = chartBarStartMs(rawTime, timeframe)
        const nextVolume = Math.round(volumeBase[i] * 100) / 100
        // Carry per-bar analytics (OI, funding, …) so cache-reading studies
        // update at the live edge, not just the price candle.
        const { analytics, rawAnalytics } = extractBarAnalytics(candleList, i)
        const existing = barsByBucket.get(alignedTime)

        if (!existing) {
          barsByBucket.set(alignedTime, {
            time: alignedTime,
            open: open[i],
            high: high[i],
            low: low[i],
            close: close[i],
            volume: nextVolume,
            analytics,
            rawAnalytics,
          })
          return
        }

        existing.high = Math.max(existing.high, high[i])
        existing.low = Math.min(existing.low, low[i])
        existing.close = close[i]
        existing.volume = nextVolume
        // Latest tick in the bucket wins (close semantics).
        existing.analytics = analytics
        existing.rawAnalytics = rawAnalytics
      })

      const bars = Array.from(barsByBucket.values()).sort((a, b) => a.time - b.time)

      if (bars.length === 0) return

      const firstIncomingBar = bars[0]
      if (
        lastPushedBarTimeRef.current > 0 &&
        firstIncomingBar.time > nextChartBarStartMs(lastPushedBarTimeRef.current, timeframe)
      ) {
        await backfillMissingBars(firstIncomingBar.time - 1)
        if (activeStreamKeyRef.current !== activeStreamKey) return
      }

      pushBars(bars)

      const lastBar = bars[bars.length - 1]
      onPriceUpdate?.(lastBar.close)
    },
    [
      symbol,
      exchangeEnum,
      timeframe,
      datafeedRef,
      lastPushedBarTimeRef,
      activeStreamKey,
      backfillMissingBars,
      pushBars,
      onPriceUpdate,
    ]
  )

  const handleChartMessage = useCallback(
    (chartData: ChartData | undefined) => {
      messageQueueRef.current = messageQueueRef.current
        .then(() => processChartMessage(chartData))
        .catch((error) => {
          console.warn('[useChartRealtimeBridge] Failed to process chart message:', error)
        })
    },
    [processChartMessage]
  )

  const { connectionStatus } = useChartStream({
    connected: isChartReady && enabled,
    payload: { symbol, exchange: exchangeEnum, timeframe },
    onMessage: handleChartMessage,
  })

  useEffect(() => {
    messageQueueRef.current = Promise.resolve()
    previousConnectionStatusRef.current = null
  }, [symbol, exchangeEnum, timeframe])

  useEffect(() => {
    const previousStatus = previousConnectionStatusRef.current
    previousConnectionStatusRef.current = connectionStatus

    if (!isChartReady || !enabled || connectionStatus !== 'OPEN' || previousStatus === null) return
    if (previousStatus === 'OPEN') return

    messageQueueRef.current = messageQueueRef.current.then(() => backfillMissingBars(Date.now()))
  }, [backfillMissingBars, connectionStatus, enabled, isChartReady])

  useEffect(() => {
    if (!isChartReady || !enabled) return

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      messageQueueRef.current = messageQueueRef.current.then(() => backfillMissingBars(Date.now()))
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [backfillMissingBars, enabled, isChartReady])
}
